import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, MessageCircle, StickyNote, Send, AlertTriangle } from 'lucide-react';
import TeamResponses from './TeamResponses';
import DependencyIndicator from './DependencyIndicator';
import ThreadedComment from './ThreadedComment';
import CostIndicator from './CostIndicator';
import SupportLinks from './SupportLinks';

const AdminFeatureCard = ({
    feature,
    responses,
    comments = [],
    reactions = [],
    dependencies,
    conflicts,
    isExpanded,
    onToggleExpand,
    onAddComment,
    onAddNote,
    onDeleteComment,
    onDeleteNote,
    adminComments,
    adminNotes,
    organization,
}) => {
    const [newComment, setNewComment] = useState('');
    const [newNote, setNewNote] = useState('');

    // Filter team responses for current organization
    const teamResponses = useMemo(() => {
        return responses?.filter(r => r.organization_id === organization?.id) || [];
    }, [responses, organization?.id]);

    const commentCount = comments.length;

    const handleCommentSubmit = (e) => {
        e.preventDefault();
        if (newComment.trim()) {
            onAddComment(feature.reference, newComment.trim());
            setNewComment('');
        }
    };

    const handleNoteSubmit = (e) => {
        e.preventDefault();
        if (newNote.trim()) {
            onAddNote(feature.reference, newNote.trim());
            setNewNote('');
        }
    };

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            {/* Header */}
            <div onClick={onToggleExpand} className="flex justify-between items-center cursor-pointer">
                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-500">{feature.reference}</span>
                        <h3 className="font-medium">{feature.name}</h3>
                    </div>
                    {feature.cost_indicator && <CostIndicator cost={feature.cost_indicator} />}
                </div>

                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                        <MessageCircle className="w-4 h-4" />
                        <span>{commentCount} {commentCount === 1 ? 'comment' : 'comments'}</span>
                    </div>
                    {isExpanded ? (
                        <ChevronUp className="w-5 h-5" />
                    ) : (
                        <ChevronDown className="w-5 h-5" />
                    )}
                </div>
            </div>

            {isExpanded && (
                <div className="mt-4 space-y-6">
                    {/* Description */}
                    <p className="text-gray-600">{feature.description}</p>

                    {/* Support Links */}
                    <SupportLinks
                        docUrl={feature.support_doc_url}
                        videoUrl={feature.support_video_url}
                    />

                    {/* Warning Message */}
                    {feature.warning_message && (
                        <div className="bg-amber-50 p-4 rounded-lg border border-amber-100">
                            <div className="flex items-center gap-2">
                                <AlertTriangle className="w-4 h-4 text-amber-500" />
                                <p className="text-sm text-amber-700">{feature.warning_message}</p>
                            </div>
                        </div>
                    )}

                    {/* Dependencies */}
                    {dependencies && dependencies.length > 0 && (
                        <DependencyIndicator
                            feature={feature}
                            userResponse={responses.find(r => r.feature_reference === feature.reference)}
                            teamResponses={teamResponses}
                            featureDependencies={dependencies}
                            organization={organization}
                        />
                    )}

                    {/* Admin Tools Section */}
                    <div className="grid grid-cols-2 gap-4">
                        {/* Admin Comments */}
                        <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                            <h4 className="text-sm font-medium text-amber-900 mb-3 flex items-center gap-2">
                                <MessageCircle className="w-4 h-4" />
                                Admin Comments
                            </h4>
                            
                            <div className="space-y-3 mb-4">
                                {adminComments[feature.reference]?.map(comment => (
                                    <div key={comment.id} className="bg-white p-3 rounded-lg border border-amber-100">
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <span className="font-medium text-amber-900">
                                                    {comment.user_name}
                                                </span>
                                                <span className="text-xs text-amber-700 ml-2">
                                                    {new Date(comment.created_at).toLocaleString()}
                                                </span>
                                            </div>
                                            <button
                                                onClick={() => onDeleteComment(comment.id)}
                                                className="text-amber-700 hover:text-amber-900"
                                            >
                                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                                                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                            </button>
                                        </div>
                                        <p className="mt-1 text-amber-800">{comment.comment_text}</p>
                                    </div>
                                ))}
                            </div>

                            <form onSubmit={handleCommentSubmit} className="flex gap-2">
                                <input
                                    type="text"
                                    value={newComment}
                                    onChange={(e) => setNewComment(e.target.value)}
                                    placeholder="Add admin comment..."
                                    className="flex-1 px-3 py-2 border border-amber-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                />
                                <button
                                    type="submit"
                                    disabled={!newComment.trim()}
                                    className="px-3 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
                                >
                                    <Send className="w-4 h-4" />
                                </button>
                            </form>
                        </div>

                        {/* Private Notes */}
                        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                            <h4 className="text-sm font-medium text-blue-900 mb-3 flex items-center gap-2">
                                <StickyNote className="w-4 h-4" />
                                Private Notes
                            </h4>
                            
                            <div className="space-y-3 mb-4">
                                {adminNotes[feature.reference]?.map(note => (
                                    <div key={note.id} className="bg-white p-3 rounded-lg border border-blue-100">
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <span className="font-medium text-blue-900">
                                                    {note.user_name}
                                                </span>
                                                <span className="text-xs text-blue-700 ml-2">
                                                    {new Date(note.created_at).toLocaleString()}
                                                </span>
                                            </div>
                                            <button
                                                onClick={() => onDeleteNote(note.id)}
                                                className="text-blue-700 hover:text-blue-900"
                                            >
                                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                                                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                            </button>
                                        </div>
                                        <p className="mt-1 text-blue-800">{note.note_text}</p>
                                    </div>
                                ))}
                            </div>

                            <form onSubmit={handleNoteSubmit} className="flex gap-2">
                                <input
                                    type="text"
                                    value={newNote}
                                    onChange={(e) => setNewNote(e.target.value)}
                                    placeholder="Add private note..."
                                    className="flex-1 px-3 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                                <button
                                    type="submit"
                                    disabled={!newNote.trim()}
                                    className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                                >
                                    <Send className="w-4 h-4" />
                                </button>
                            </form>
                        </div>
                    </div>

                    {/* Team Responses */}
                    <div className="mt-6">
                        <TeamResponses
                            responses={teamResponses}
                            featureRef={feature.reference}
                            organization={organization}
                        />
                    </div>

                    {/* User Comments */}
                    <div className="pt-4 border-t">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <MessageCircle className="w-5 h-5 text-gray-400" />
                                <span className="text-sm font-medium text-gray-900">
                                    User Comments ({commentCount})
                                </span>
                            </div>
                        </div>
                        
                        <div className="space-y-4">
                            {comments.map(comment => (
                                <ThreadedComment
                                    key={comment.id}
                                    comment={comment}
                                    reactions={reactions.filter(r => r.comment_id === comment.id)}
                                    readOnly={true}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminFeatureCard;